import { useState, useCallback } from 'react';
import { useQuery, useMutation, usePaginatedQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a local convex/ directory file
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: Convex generated dataModel lives outside src/
import type {
  IUseNotificationsPaginatedReturn,
  TNotificationCategory,
} from '@/@types/notificationPanel';

const INITIAL_PAGE_SIZE = 20;
const LOAD_MORE_SIZE = 15;

export function useNotifications(): IUseNotificationsPaginatedReturn {
  const [activeCategory, setActiveCategory] = useState<TNotificationCategory>('all');

  const {
    results: notifications,
    status: paginationStatus,
    loadMore: loadMoreRaw,
  } = usePaginatedQuery(
    api.insuranceNotifications.queries.listForUserPaginated,
    { category: activeCategory },
    { initialNumItems: INITIAL_PAGE_SIZE }
  );

  const unreadCountByCategory = useQuery(api.insuranceNotifications.queries.unreadCountByCategory);

  const markReadMutation = useMutation(api.insuranceNotifications.mutations.markRead);
  const markAllReadMutation = useMutation(api.insuranceNotifications.mutations.markAllRead);
  const acknowledgeMutation = useMutation(
    api.insuranceNotifications.mutations.acknowledgeNotification
  );

  const markRead = useCallback(
    (notificationId: Id<'insuranceNotifications'>) => {
      void markReadMutation({ notificationId });
    },
    [markReadMutation]
  );

  const markAllRead = useCallback(() => {
    void markAllReadMutation({});
  }, [markAllReadMutation]);

  const acknowledge = useCallback(
    (notificationId: Id<'insuranceNotifications'>) => {
      void acknowledgeMutation({ notificationId });
    },
    [acknowledgeMutation]
  );

  const loadMore = useCallback(() => {
    if (paginationStatus === 'CanLoadMore') {
      loadMoreRaw(LOAD_MORE_SIZE);
    }
  }, [paginationStatus, loadMoreRaw]);

  const defaultCounts: Record<TNotificationCategory, number> = {
    all: 0,
    claims: 0,
    policies: 0,
    preauth: 0,
    invoices: 0,
  };

  return {
    notifications: notifications ?? [],
    unreadCount: unreadCountByCategory?.all ?? 0,
    unreadCountByCategory: unreadCountByCategory ?? defaultCounts,
    activeCategory,
    setActiveCategory,
    markRead,
    markAllRead,
    acknowledge,
    isLoading: paginationStatus === 'LoadingFirstPage',
    loadMore,
    hasMore: paginationStatus === 'CanLoadMore',
    isLoadingMore: paginationStatus === 'LoadingMore',
  };
}
